import { useMemo } from 'react';

import { isPartialPolicyPayload, TypedPolicy } from '../../../types';

import {
  DOCKER_SETUP_FORM_INITIAL_VALUES,
  DOCKER_SETUP_INITIAL_VALUES,
  DockerSetupFormData,
  DockerSetupFormValues,
  DockerSetupPolicyPayload,
} from './types';

/**
 * Type guard to check if data is DockerSetupPolicyPayload
 */
function isDockerSetupPolicyPayload(
  data: unknown
): data is DockerSetupPolicyPayload {
  return (
    typeof data === 'object' &&
    data !== null &&
    'enableHostManagementFeatures' in data
  );
}

/**
 * Hook to get initial values for Docker Setup Policy form.
 *
 * Returns complete form values including common fields (name, environmentGroupIds)
 * and type-specific fields.
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @param templateData - Optional template data to pre-fill form (for create from template)
 * @returns Complete form initial values
 */
export function useDockerSetupInitialValues(
  policy?: TypedPolicy<unknown>,
  templateData?: Record<string, unknown>
): DockerSetupFormValues {
  return useMemo(() => {
    const defaults = DOCKER_SETUP_INITIAL_VALUES;

    // Template mode: apply template data over defaults
    if (
      !policy &&
      templateData &&
      isPartialPolicyPayload<DockerSetupPolicyPayload>(templateData)
    ) {
      return {
        name: '',
        environmentGroupIds: [],
        ...deserializePayload(templateData, defaults),
      };
    }

    // Create mode: use defaults
    if (!policy) {
      return DOCKER_SETUP_FORM_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!isDockerSetupPolicyPayload(data)) {
      return {
        // Common fields from policy metadata
        name: policy.Name,
        environmentGroupIds: policy.EnvironmentGroups ?? [],
        // Type-specific defaults (using type-specific only, not full form values)
        ...defaults,
      };
    }

    return {
      // Common fields from policy metadata
      name: policy.Name,
      environmentGroupIds: policy.EnvironmentGroups ?? [],
      ...deserializePayload(data, defaults),
    };
  }, [policy, templateData]);
}

/**
 * Deserialize a Docker Setup Policy Payload into form values.
 * Used for both edit mode and template mode
 *
 * @param data The Existing policy data, either an existing to be edited or a template
 * @param defaults The default values that should be used if any form values are missing in data
 */
function deserializePayload(
  data: Partial<DockerSetupPolicyPayload>,
  defaults: DockerSetupFormData
) {
  return {
    // Type-specific fields from policy data
    enableHostManagementFeatures:
      data.enableHostManagementFeatures?.value ??
      defaults.enableHostManagementFeatures,
    allowVolumeBrowserForRegularUsers:
      data.allowVolumeBrowserForRegularUsers?.value ??
      defaults.allowVolumeBrowserForRegularUsers,
    enableGpuManagement:
      data.enableGpuManagement?.value ?? defaults.enableGpuManagement,
    enableImageNotification:
      data.enableImageNotification?.value ?? defaults.enableImageNotification,
    gpus: data.gpus?.value ?? defaults.gpus,
    changeWindow: data.endpointChangeWindow?.value ?? defaults.changeWindow,
  };
}
